import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Brain, Trophy, TrendingDown, Megaphone, ListChecks, Gift, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const KIND_DOT = { overdue: "bg-red-400", payment: "bg-amber-400", promo: "bg-[#F2B705]", leads: "bg-sky-400", club: "bg-emerald-400", winback: "bg-purple-400" };

export default function AiInsights({ token }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/system/insights`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center gap-2 text-white/50 py-8 justify-center" data-testid="insights-loading"><Loader2 className="animate-spin" size={16} /> Analyzing your business…</div>;
  if (!d) return null;

  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5" data-testid="ai-insights">
      <h3 className="font-display font-black text-lg flex items-center gap-2">
        <Brain size={18} className="text-[#F2B705]" /> AI Business Assistant
        <span className="text-white/30 text-sm font-normal">— what to act on, not just charts</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div data-testid="insight-top-vehicle" className="bg-[#0A0A0A] border border-emerald-500/25 rounded-xl p-4">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest"><Trophy size={13} /> Top Earner (Month)</div>
          {d.top_vehicle ? (
            <><div className="text-white font-display font-bold mt-1.5">{d.top_vehicle.vehicle}</div>
            <div className="text-emerald-400 font-black text-xl">{money(d.top_vehicle.month_revenue)}</div></>
          ) : <div className="text-white/40 text-sm mt-2">No revenue recorded this month yet.</div>}
        </div>
        <div data-testid="insight-low-vehicle" className="bg-[#0A0A0A] border border-amber-500/25 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-widest"><TrendingDown size={13} /> Not Renting</div>
          {d.low_vehicle ? (
            <><div className="text-white font-display font-bold mt-1.5">{d.low_vehicle.vehicle}</div>
            <div className="text-white/50 text-sm">{d.low_vehicle.rented_days_30d} rented day(s) in 30 days — promote it</div></>
          ) : <div className="text-white/40 text-sm mt-2">Every car is renting well. 🚗</div>}
        </div>
        <div data-testid="insight-best-source" className="bg-[#0A0A0A] border border-sky-500/25 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sky-400 text-xs uppercase tracking-widest"><Megaphone size={13} /> Best Marketing Source</div>
          {d.best_source ? (
            <><div className="text-white font-display font-bold mt-1.5">{d.best_source.source}</div>
            <div className="text-white/50 text-sm">{d.best_source.booked} booked / {d.best_source.leads} leads ({d.best_source.conversion_pct}%)</div></>
          ) : <div className="text-white/40 text-sm mt-2">No lead data yet.</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        <div data-testid="insight-actions" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest mb-2"><ListChecks size={13} className="text-[#F2B705]" /> Today's Action List</div>
          {(d.actions || []).length === 0 && <p className="text-white/40 text-sm">Nothing urgent — go get more bookings. 🎉</p>}
          <div className="space-y-1.5">
            {(d.actions || []).map((a, i) => (
              <div key={i} data-testid={`insight-action-${i}`} className="flex items-start gap-2 text-sm text-white/80">
                <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${KIND_DOT[a.kind] || "bg-white/40"}`} />
                {a.text}
              </div>
            ))}
          </div>
        </div>

        <div data-testid="insight-winback" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest mb-2"><RotateCcw size={13} className="text-purple-400" /> Send These a Promo</div>
          {(d.promo_candidates || []).length === 0 && <p className="text-white/40 text-sm">No inactive past customers right now.</p>}
          <div className="space-y-1.5">
            {(d.promo_candidates || []).map((c) => (
              <div key={c.email} className="flex items-center justify-between text-sm">
                <span className="text-white/85 truncate mr-2">{c.name}</span>
                <span className="text-white/45 text-xs whitespace-nowrap">{money(c.spend)} · {c.days_inactive}d quiet</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="insight-near-reward" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest mb-2"><Gift size={13} className="text-emerald-400" /> Close to a Reward</div>
          {(d.near_reward || []).length === 0 && <p className="text-white/40 text-sm">No club members near a reward yet.</p>}
          <div className="space-y-1.5">
            {(d.near_reward || []).map((m) => (
              <div key={m.email} className="flex items-center justify-between text-sm">
                <span className="text-white/85 truncate mr-2">{m.name}</span>
                <span className="text-emerald-400/90 text-xs whitespace-nowrap">{m.needed.toLocaleString()} pts from {m.target}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
